import type { ComponentType } from 'react';
import React, { createElement, useMemo, useRef, useCallback } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import type {
	DefinitionType,
	createLoadableComponentProps,
	LoadedModuleType,
	LoadableType,
	ModuleType,
	ActivationReason,
} from './types';
import { LoadingPriority } from './constants';
import { LoadableAncestorsProvider } from './LoadableAncestors';
import { usePlaceholderId } from './placeholders/usePlaceholderId';
import { makeGate, unlockGate } from './suspense/Gate';
import type { UnlockReason } from './suspense/Gate';
import { Boundary } from './suspense/Boundary';
import { useActivationTriggers } from './suspense/useActivationTriggers';

function hasDefault<P>(
	m: ModuleType<P> | { default: ModuleType<P> },
): m is { default: ModuleType<P> } {
	return typeof m === 'object' && m !== null && 'default' in m;
}

export function createSuspenseClientComponent<P extends { immediateActivationCondition?: boolean }>(
	{ __loadable_id__, loader, name, triggers, ssrSuccessCheckName }: DefinitionType<P>,
	{ loadableName, loadedModuleRef, displayName }: createLoadableComponentProps<P>,
) {
	const LoadableSuspenseClientComponent = (props: P) => {
		const { immediateActivationCondition } = props;
		const hostRef = useRef<HTMLDivElement | null>(null);
		const baseId = __loadable_id__ || name || loadableName;
		const placeholderId = usePlaceholderId(baseId);
		const gate = useMemo(() => makeGate(), []);
		const loaderPromiseRef = useRef<Promise<void> | null>(null);
		const startTimeRef = useRef<number | null>(null);
		const { createAnalyticsEvent } = useAnalyticsEvents();

		const renderChildren = useCallback(() => {
			if (loadedModuleRef.ref) {
				return createElement(loadedModuleRef.ref as ComponentType<P>, props);
			}
			return null;
		}, [props]);

		const onLoaded = useCallback((m: LoadedModuleType<P>) => {
			loadedModuleRef.ref = hasDefault(m) ? m.default : m;
		}, []);

		const activateComponent = useCallback(
			(reason: ActivationReason, unlockReason?: UnlockReason) => {
				if (!loadedModuleRef.ref && !startTimeRef.current) {
					startTimeRef.current = performance.now();
				}
				if (gate?.getStatus() === 'suspended') {
					createAnalyticsEvent({
						type: 'sendOperationalEvent',
						data: {
							source: 'loadable',
							action: 'activated',
							actionSubject: 'loadableComponent',
							attributes: {
								loadableName,
								triggers,
								placeholderId,
								activationReason: reason,
								unlockReason,
							},
						},
					}).fire();

					void unlockGate(
						gate,
						placeholderId,
						loader,
						hostRef,
						onLoaded,
						loaderPromiseRef,
						createAnalyticsEvent,
					);
				}
			},
			[gate, placeholderId, hostRef, onLoaded, createAnalyticsEvent],
		);

		const hostRefWithActivation = useActivationTriggers({
			triggers,
			gate,
			activateComponent,
			boundaryId: placeholderId,
			analyticsName: loadableName,
			analyticsSource: 'loadable',
			immediateActivationCondition,
		});

		return (
			<LoadableAncestorsProvider
				id={__loadable_id__}
				name={loadableName}
				priority={LoadingPriority.ACTIVATABLE}
			>
				<div ref={hostRefWithActivation} data-hydration-shield={placeholderId}>
					<Boundary
						id={placeholderId}
						loader={loader}
						hostRef={hostRef}
						gate={gate}
						onLoaded={onLoaded}
						resolved={gate?.getStatus?.() === 'resolved'}
						render={renderChildren}
						loaderPromiseRef={loaderPromiseRef}
						boundaryName={loadableName}
						ssrSuccessCheckName={ssrSuccessCheckName}
						activateComponent={activateComponent}
						immediateActivationCondition={immediateActivationCondition}
					/>
				</div>
			</LoadableAncestorsProvider>
		);
	};

	LoadableSuspenseClientComponent.displayName = displayName;

	return LoadableSuspenseClientComponent as LoadableType<P>;
}
